import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SimpleRichEditor from './SimpleRichEditor';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white font-medium z-50 animate-slide-in ${
      type === 'success' ? 'bg-green-600' : 'bg-red-600'
    }`}>
      <div className="flex items-center gap-2">
        <span>{type === 'success' ? '✅' : '❌'}</span>
        <span>{message}</span>
        <button 
          onClick={onClose}
          className="ml-2 text-white hover:text-gray-200"
        >
          ×
        </button>
      </div>
    </div>
  );
};

interface Job {
  _id: string;
  title: string;
  content: string;
  isPublished: boolean;
  priority: number;
  createdAt?: string;
  updatedAt?: string;
}

interface JobFormData {
  title: string;
  content: string;
  isPublished: boolean;
  priority: number;
}

const JobsManager: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Job | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const [formData, setFormData] = useState<JobFormData>({
    title: '',
    content: '',
    isPublished: true,
    priority: 0
  });

  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Fetch jobs
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/jobs');
      // Handle both formats: array or {success, data}
      const jobsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setJobs(jobsData);
    } catch (err: any) {
      console.error('Error fetching jobs:', err);
      showToast('Failed to load jobs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'priority' ? parseInt(value) || 0 : value)
    }));
  };

  const handleContentChange = (content: string) => {
    setFormData(prev => ({
      ...prev,
      content
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingItem) {
        // Update existing job
        const response = await axios.put(
          `/api/jobs/${editingItem._id}`,
          formData
        );
        
        if (response.data.success) {
          showToast('Job updated successfully!', 'success');
          fetchJobs();
          resetForm();
        }
      } else {
        // Create new job
        const response = await axios.post(
          '/api/jobs',
          formData
        );
        
        if (response.data.success) {
          showToast('Job created successfully!', 'success');
          fetchJobs();
          resetForm();
        }
      }
    } catch (err: any) {
      console.error('Error saving job:', err);
      showToast(err.response?.data?.message || 'Failed to save job', 'error');
    }
  };

  const handleEdit = (job: Job) => {
    setEditingItem(job);
    setFormData({
      title: job.title,
      content: job.content,
      isPublished: job.isPublished,
      priority: job.priority
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      // 🔐 SECURITY: Use httpOnly cookies (sent automatically with withCredentials)
      const response = await axios.delete(`/api/jobs/${id}`, {
        withCredentials: true
      });

      if (response.data.success) {
        showToast('Job deleted successfully!', 'success');
        fetchJobs();
      }
    } catch (err: any) {
      console.error('Error deleting job:', err);
      showToast(err.response?.data?.message || 'Failed to delete job', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      isPublished: true,
      priority: 0
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast 
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Jobs</h1>
            <p className="text-gray-400">Manage DUSKPROOF job openings and career opportunities</p>
          </div>
        </div>

        {/* Search and Add Button Row */}
        <div className="flex gap-4 mb-8">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 w-full bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
            />
            <svg className="w-5 h-5 text-[var(--dusk-faint)] absolute left-3 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
            className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40 flex items-center gap-2"
          >
            <span>+</span>
            <span>Add Job</span>
          </button>
        </div>

        {/* Job Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-8 space-y-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                placeholder="Job title..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Content *
              </label>
              <SimpleRichEditor
                value={formData.content}
                onChange={handleContentChange}
                placeholder="Job description and requirements..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Priority
                </label>
                <input
                  type="number"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                  placeholder="0"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isPublished"
                  id="isPublished"
                  checked={formData.isPublished}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-dusk-copper bg-[rgba(243,235,226,0.06)] border-[var(--dusk-line-strong)] rounded focus:ring-dusk-copper"
                />
                <label htmlFor="isPublished" className="ml-2 text-sm text-gray-300">
                  Published
                </label>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-teal-700 to-cyan-700 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-teal-500/30"
              >
                {editingItem ? 'Update Job' : 'Create Job'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-[rgba(243,235,226,0.06)]/50 hover:bg-gray-600/50 text-gray-200 font-semibold rounded-xl transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Jobs Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Job
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {loading ? (
                  <tr>
                    <td colSpan={2} className="px-6 py-8 text-center text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="px-6 py-8 text-center text-gray-400">
                      No data
                    </td>
                  </tr>
                ) : (
                  filteredJobs.map((job) => (
                    <tr key={job._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white mb-1">{job.title}</div>
                        <div 
                          className="text-sm text-gray-400 line-clamp-2 max-w-2xl"
                          dangerouslySetInnerHTML={{ __html: job.content.substring(0, 150) + '...' }}
                        />
                        <div className="flex gap-3 mt-2 text-xs text-[var(--dusk-faint)]">
                          <span>Priority: {job.priority}</span>
                          <span>{job.isPublished ? '✅ Published' : '❌ Draft'}</span>
                          {job.createdAt && (
                            <span>Created: {new Date(job.createdAt).toLocaleDateString()}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(job)}
                            className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all"
                            title="Edit"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(job._id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsManager;
